import { useEffect, useRef, useState } from "react";
import { Beaker, Check, ChevronDown, LockKeyhole, ShieldCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAmbiente } from "../context/AmbienteContext";
import { useAuth } from "../context/AuthContext";
import { humanizeError } from "../lib/api";

const ENVIRONMENTS = {
  "00": {
    label: "Pruebas MH",
    eyebrow: "Ambiente 00",
    description: "Valida tu configuración sin efectos fiscales.",
    action: "/dashboard",
    Icon: Beaker,
  },
  "01": {
    label: "Producción",
    eyebrow: "Ambiente 01",
    description: "Emite documentos fiscales reales.",
    action: "/dashboard",
    Icon: ShieldCheck,
  },
};

export default function AmbienteSwitcher({ onNavigate }) {
  const { user } = useAuth();
  const { ambiente, loading, setAmbiente } = useAmbiente();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const isAdmin = ["administrador", "owner", "admin"].includes((user?.role || "").toLowerCase());
  const active = ENVIRONMENTS[ambiente] || ENVIRONMENTS["01"];
  const ActiveIcon = active.Icon;

  useEffect(() => {
    if (!open) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!containerRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  const chooseEnvironment = async (next) => {
    if (busy || loading || next === ambiente) {
      setOpen(false);
      return;
    }
    if (next === "01" && !isAdmin) {
      toast.error("Producción requiere permisos de administrador.");
      return;
    }
    setBusy(true);
    try {
      await setAmbiente(next);
      try { window.localStorage.setItem("dteflow_ambiente_chosen", "1"); } catch (_) {}
      setOpen(false);
      toast.success(next === "00" ? "Ambiente de pruebas activado" : "Producción activada");
      navigate(ENVIRONMENTS[next].action);
      onNavigate?.();
    } catch (error) {
      toast.error(humanizeError(error, "No se pudo cambiar de ambiente"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="environment-switcher" ref={containerRef} data-testid="environment-switcher">
      <button
        type="button"
        className="environment-switcher-trigger"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={`Ambiente activo: ${active.label}. Cambiar ambiente`}
        disabled={loading || busy}
      >
        <span className="environment-switcher-icon" aria-hidden="true">
          <ActiveIcon className="h-4 w-4" strokeWidth={2} />
        </span>
        <span className="min-w-0 flex-1 text-left">
          <span className="environment-switcher-caption">Ambiente activo</span>
          <span className="environment-switcher-label">{active.label}</span>
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>

      {open && (
        <div className="environment-switcher-menu" role="menu" aria-label="Cambiar ambiente">
          <div className="environment-switcher-menu-header">
            <span className="environment-switcher-caption">Espacio de trabajo</span>
            <span className="text-[11px] text-muted-foreground">Solo afecta tu sesión</span>
          </div>
          {Object.entries(ENVIRONMENTS).map(([key, option]) => {
            const OptionIcon = option.Icon;
            const selected = ambiente === key;
            const locked = key === "01" && !isAdmin;
            return (
              <button
                key={key}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                className={`environment-switcher-option ${selected ? "is-selected" : ""}`}
                onClick={() => chooseEnvironment(key)}
                disabled={busy || loading || locked}
                data-testid={`environment-option-${key}`}
              >
                <span className="environment-option-icon" aria-hidden="true">
                  <OptionIcon className="h-4 w-4" strokeWidth={2} />
                </span>
                <span className="min-w-0 flex-1 text-left">
                  <span className="environment-option-title">{option.label}</span>
                  <span className="environment-option-meta">{option.eyebrow} · {option.description}</span>
                </span>
                {locked ? <LockKeyhole className="h-3.5 w-3.5 shrink-0" aria-label="Requiere permisos" /> : null}
                {selected ? <Check className="h-4 w-4 shrink-0" strokeWidth={2.5} aria-label="Seleccionado" /> : null}
              </button>
            );
          })}
          <div className="environment-switcher-note">
            <span className="font-medium">Separación por ambiente</span>
            <span>Documentos, credenciales y pruebas permanecen aislados.</span>
          </div>
        </div>
      )}
    </div>
  );
}
